import React, { memo, useMemo, useCallback } from 'react'

import { arrayOf, bool, string } from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { trackSettingsInteraction } from 'Pods/ThreeDStudio/Services/studioTracker'
import ButtonGroupSelector from 'Pods/ThreeDStudio/Shared/ButtonGroupSelector/ButtonGroupSelector'
import { providerSupportsAction } from 'Pods/ThreeDStudio/Shared/Utils/providerSupportsAction'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { resetAnimationUnsupportedByProvider } from 'Pods/ThreeDStudio/Store/actions/animateSettings'
import { resetSettingsUnsupportedByProvider } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { setEnhancedProvider, setGenerationProvider } from 'Pods/ThreeDStudio/Store/actions/schema'
import { PROVIDER } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { PROVIDER_LABELS } from './providerLabels'

function AIEngineSelector({ isEnhanced = false, hiddenProviders = EMPTY_ARRAY }) {
  const t = useTranslation()
  const generationAction = useThreeDStudioStore(state => state.schema.generationAction)
  const generationProvider = useThreeDStudioStore(state => state.schema.generationProvider)
  const enhancedProvider = useThreeDStudioStore(state => state.schema.enhancedProvider)
  const availableGenerationProviders = useThreeDStudioStore(
    state => state.schema.availableGenerationProviders
  )
  const availableEnhancedProviders = useThreeDStudioStore(
    state => state.schema.availableEnhancedProviders
  )
  const provider = isEnhanced ? enhancedProvider : generationProvider
  const availableProviders = isEnhanced ? availableEnhancedProviders : availableGenerationProviders

  const setProvider = isEnhanced ? setEnhancedProvider : setGenerationProvider
  const handleProviderClick = useCallback((id) => {
    trackSettingsInteraction('ai_engine', id)
    // The engine outranks the settings it cannot run, so those are cleared before
    // it becomes active: on the generation flow an unsupported setting would keep
    // the resolved schema ineligible and the store would roll the engine back; on
    // the enhanced flow a picked animation the engine has no clip for would stay
    // selected behind a filtered grid.
    if (isEnhanced) {
      resetAnimationUnsupportedByProvider(id)
    } else {
      resetSettingsUnsupportedByProvider(id)
    }
    setProvider(id)
  }, [isEnhanced, setProvider])
  const handleHunyuanClick = useCallback(() => handleProviderClick(PROVIDER.TENCENT), [handleProviderClick])
  const handleMeshyClick = useCallback(() => handleProviderClick(PROVIDER.MESHY), [handleProviderClick])
  const handleTripo3DClick = useCallback(() => handleProviderClick(PROVIDER.TRIPO3D), [handleProviderClick])

  // On the generation flow an engine is only disabled when it cannot run the action at
  // all: the additional settings never disable it, picking it resets them instead. The
  // enhanced flow keeps the schema-driven list, where the engine *is* the mode
  // (Meshy = standard remesh, Hunyuan/Tripo3D = smart low poly).
  const isProviderDisabled = useCallback((id) => {
    if (!isEnhanced) {
      return !providerSupportsAction(generationAction, id)
    }

    return !availableProviders?.includes(id)
  }, [isEnhanced, generationAction, availableProviders])

  const buttons = useMemo(() => {
    const providerConfig = [
      { id: PROVIDER.TENCENT, onClick: handleHunyuanClick },
      { id: PROVIDER.MESHY, onClick: handleMeshyClick },
      { id: PROVIDER.TRIPO3D, onClick: handleTripo3DClick },
    ]

    return providerConfig
      .filter(({ id }) => !hiddenProviders.includes(id))
      .map(({ id, onClick }) => {
        const disabled = isProviderDisabled(id)

        return {
          id,
          label: PROVIDER_LABELS[id],
          onClick,
          disabled,
          isActive: provider === id,
          tooltipMessage: disabled ? t('shared.aiEngine.disabledTooltip') : undefined,
        }
      })
  }, [
    t,
    provider,
    hiddenProviders,
    isProviderDisabled,
    handleHunyuanClick,
    handleMeshyClick,
    handleTripo3DClick,
  ])

  // With a single provider left there is nothing to choose from - the store
  // auto-selects it (setAvailableEnhancedProviders 0/1/many rules), so the
  // selector is hidden entirely.
  if (buttons.length <= 1) {
    return null
  }

  return (
    <ButtonGroupSelector
      buttons={buttons}
      keyName="provider"
      sliceName="schema"
      label={t('shared.aiEngine.label')}
    />
  )
}

AIEngineSelector.propTypes = {
  isEnhanced: bool,
  hiddenProviders: arrayOf(string),
}

export default memo(AIEngineSelector)
